import { CloseOutlined, DownOutlined, PlusOutlined } from '@ant-design/icons';
import { Button, Dropdown, InputNumber } from 'antd';
import { memo } from 'react';
import styles from './style.module.scss';
import {
    IVesselInsightFilter,
    TVesselInsightMetric,
    TVesselInsightOperator,
    useVesselOverview,
} from './VesselOverviewContext';

interface IProps {
    filteredCount: number;
}

const INSIGHT_METRICS: { value: TVesselInsightMetric; label: string }[] = [
    { value: 'operation', label: 'Operation' },
    { value: 'hygine', label: 'Quarantine, Hygiene control' },
    { value: 'maintenance', label: 'Maintenance' },
    { value: 'idle', label: 'Idle time' },
    { value: 'travelling', label: 'Traveling time' },
];

const INSIGHT_OPERATORS: { value: string, label: string }[] = [
    { value: '<', label: 'Less than' },
    { value: '>', label: 'More than' },
    { value: '=', label: 'Equal to' },
]

const FleetInsight = ({ filteredCount }: IProps) => {
    const [insightFilters, setStore] = useVesselOverview(state => state.insightFilters);
    const [appliedInsightFilters] = useVesselOverview(state => state.appliedInsightFilters);
    const hasInsightValue = insightFilters.some(item => item.value != null);
    const isApplied = hasInsightValue
        && insightFilters.length === appliedInsightFilters.length
        && insightFilters.every((filter, index) => {
            const appliedFilter = appliedInsightFilters[index];

            return filter.metric === appliedFilter?.metric
                && filter.operator === appliedFilter.operator
                && filter.value === appliedFilter.value;
        });

    const updateFilter = (index: number, value: Partial<IVesselInsightFilter>) => {
        setStore({
            insightFilters: insightFilters.map((item, itemIndex) =>
                itemIndex === index ? { ...item, ...value } : item
            ),
        });
    };

    const addFilter = () => {
        setStore({
            insightFilters: [...insightFilters, { metric: 'operation', operator: '>' }],
        });
    };

    const removeFilter = (index: number) => {
        setStore({
            insightFilters: insightFilters.filter((_, itemIndex) => itemIndex !== index),
            appliedInsightFilters: appliedInsightFilters.filter(
                (_, itemIndex) => itemIndex !== index
            ),
        });
    };

    return <section className={styles.insight}>
        <div className='text-[15px] font-[600]'>Fleet insight</div>
        <p className="text-comment italic text-[14px]">(Set condition to get your fleet insight)</p>
        <div className='flex flex-col gap-1 mt-1'>
            {insightFilters.map((filter, index) => <div className={styles.condition} key={index}>
                <Dropdown
                    trigger={['click']}
                    menu={{
                        items: INSIGHT_METRICS.map(item => ({ key: item.value, label: item.label })),
                        selectedKeys: [filter.metric],
                        onClick: ({ key }) => updateFilter(index, { metric: key as TVesselInsightMetric }),
                    }}
                >
                    <button
                        type="button"
                        className={styles['metric-dropdown']}
                        aria-label={`Metric for condition ${index + 1}`}
                    >
                        <span>{INSIGHT_METRICS.find(item => item.value === filter.metric)?.label}</span>
                        <DownOutlined />
                    </button>
                </Dropdown>
                <Dropdown
                    trigger={['click']}
                    menu={{
                        items: INSIGHT_OPERATORS.map(item => ({ key: item.value, label: item.label })),
                        selectedKeys: [filter.operator],
                        onClick: ({ key }) => updateFilter(index, { operator: key as TVesselInsightOperator }),
                    }}
                >
                    <button
                        type="button"
                        className={styles['operator-dropdown']}
                        aria-label={`Operator for condition ${index + 1}`}
                    >
                        <span>{INSIGHT_OPERATORS.find(item => item.value === filter.operator)?.label}</span>
                        <DownOutlined />
                    </button>
                </Dropdown>
                <InputNumber
                    className='px-0 overflow-hidden w-max-[52px]'
                    variant="borderless"
                    aria-label={`Percentage for condition ${index + 1}`}
                    min={0}
                    max={100}
                    controls={false}
                    placeholder="value"
                    value={filter.value}
                    suffix="%"
                    onChange={value => updateFilter(index, {
                        value: value == null ? undefined : value,
                    })}
                />
                <Button
                    className="bt-ghost !border-0"
                    data-icon="true"
                    data-danger="true"
                    aria-label={`Remove condition ${index + 1}`}
                    onClick={() => removeFilter(index)}
                >
                    <CloseOutlined />
                </Button>
            </div>)}
        </div>
        {hasInsightValue &&
            <button
                type="button"
                className={`${styles.chip} ${isApplied ? styles.active : ''}`}
                aria-pressed={isApplied}
                onClick={() => setStore({
                    appliedInsightFilters: isApplied
                        ? []
                        : insightFilters.map(item => ({ ...item })),
                })}
            >
                {filteredCount} vessels
            </button>}
        <Button className='bt-ghost mt-2' data-icon="true" aria-label="Add insight condition" onClick={addFilter}>
            <PlusOutlined />
        </Button>
    </section>;
};

export default memo(FleetInsight);
